import { getCountries, getCountryCallingCode, type CountryCode } from 'libphonenumber-js/min';

export const EXCLUDED_COUNTRIES: readonly CountryCode[] = ['IL'];

export interface CountryOption {
  code: CountryCode;
  name: string;
  englishName: string;
  dialCode: string;
  searchHaystack: string;
}

export function flagUrl(code: string, w: 20 | 40 | 80 = 40): string {
  return `https://flagcdn.com/w${w}/${code.toLowerCase()}.png`;
}

// Hand-picked aliases so users can find a country by the colloquial name
// they're likely to type even when it differs from the official ISO label
// ("Holland" not "Nederland"; "UK" not "United Kingdom"; etc).
const ALIASES: Partial<Record<CountryCode, readonly string[]>> = {
  NL: ['holland', 'the netherlands', 'nederland'],
  GB: ['uk', 'great britain', 'england', 'britain', 'scotland', 'wales', 'northern ireland'],
  US: ['usa', 'america', 'united states', 'the us', 'us'],
  KR: ['south korea', 'republic of korea'],
  KP: ['north korea'],
  CZ: ['czech republic', 'czechia'],
  CD: ['drc', 'congo kinshasa'],
  CG: ['congo brazzaville'],
  TR: ['turkiye', 'turkey'],
  MM: ['burma', 'myanmar'],
  SZ: ['swaziland', 'eswatini'],
  CV: ['cape verde', 'cabo verde'],
  TL: ['east timor', 'timor leste'],
  CI: ['ivory coast', 'cote divoire'],
  MK: ['macedonia', 'north macedonia'],
};

function regionName(code: string, locale: string): string {
  try {
    const dn = new Intl.DisplayNames([locale, 'en'], { type: 'region' });
    return dn.of(code) || code;
  } catch {
    return code;
  }
}

let cache: { locale: string; options: CountryOption[] } | null = null;

export function getCountryOptions(locale: string): CountryOption[] {
  if (cache && cache.locale === locale) return cache.options;
  const options = getCountries()
    .filter((c) => !EXCLUDED_COUNTRIES.includes(c))
    .map((code) => {
      const name = regionName(code, locale);
      const englishName = regionName(code, 'en');
      const aliases = (ALIASES[code] ?? []).join(' ');
      return {
        code,
        name,
        englishName,
        dialCode: '+' + getCountryCallingCode(code),
        searchHaystack: `${name} ${englishName} ${code} ${aliases}`.toLowerCase(),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, locale));
  cache = { locale, options };
  return options;
}

// Tokenized substring match: every whitespace-separated token in the
// query must appear somewhere in the haystack. Lets "the ne" find "The
// Netherlands" via the alias, "south kor" find "South Korea", etc.
export function filterCountries(options: readonly CountryOption[], query: string): CountryOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options.slice();
  const tokens = q.split(/\s+/);
  return options.filter((o) => tokens.every((t) => o.searchHaystack.includes(t)));
}
